// Analytics workbench tab (approved design: docs/design/analytics_workbench_v2_spec.md).
// Left rail (Overview / Connections / Business Areas / Schema Design /
// Visuals / Dashboards + "Build analytics" CTA + the "What the letters mean"
// legend) and a main area hosting one section at a time. Sections compose
// the EXISTING designers (Business Area / Schema / Visual / Dashboard) and
// the shipped connections hub — this tab is chrome, not a re-implementation.
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { ChevronRight, Sparkles } from 'lucide-react'
import { useProjectStore } from '@/store/projectStore'
import { listBiConnections } from '@/api/connections'
import {
  listBusinessAreas,
  listProjectPages,
  listProjectReports,
  listVisualSchemas,
  objectCatalog,
} from '@/api/designers'
import { listProjectProcesses } from '@/api/genprocess'
import { listForesight } from '@/api/foresight'
import { listStandardizeRequests } from '@/api/standardize'
import { EMPTY_INVENTORY, type AnalyticsInventory } from './analytics/AnalyticsTypes'
import {
  OBJECT_TYPE_LEGEND,
  RAIL_ITEMS,
  TypeChip,
  type AnalyticsSection,
} from './analytics/shared'
import OverviewSection from './analytics/OverviewSection'
import ConnectionsSection from './analytics/ConnectionsSection'
import BusinessAreasSection from './analytics/BusinessAreasSection'
import SchemaSection from './analytics/SchemaSection'
import VisualsSection from './analytics/VisualsSection'
import DashboardsSection from './analytics/DashboardsSection'
import ReportsSection from './analytics/ReportsSection'
import ProcessesSection from './analytics/ProcessesSection'
import SmartPagesSection from './analytics/SmartPagesSection'
import ForesightSection from './analytics/ForesightSection'
import GuidedBuildSection from './analytics/GuidedBuildSection'

interface OpenTarget {
  objName?: string
  visualsTab?: 'mine' | 'standardised'
  nonce: number
}

const sectionKey = (project: string) => `twasta.analytics.section.${project}`

export default function AnalyticsTab() {
  const { currentProject } = useProjectStore()
  const project = currentProject?.application.folder_name || ''

  const [section, setSection] = useState<AnalyticsSection>(() => {
    const saved = project ? sessionStorage.getItem(sectionKey(project)) : null
    const valid: AnalyticsSection[] = [
      'overview', 'connections', 'areas', 'schema', 'visuals', 'dashboards', 'reports',
      'processes', 'smartpages', 'foresight', 'build',
    ]
    return valid.includes(saved as AnalyticsSection) ? (saved as AnalyticsSection) : 'overview'
  })
  const [target, setTarget] = useState<OpenTarget>({ nonce: 0 })
  const [legendOpen, setLegendOpen] = useState(false)

  useEffect(() => {
    if (project) sessionStorage.setItem(sectionKey(project), section)
  }, [project, section])

  // ── Unsaved-changes guard from the object sections ──
  // A master-detail section registers a guard while its embedded designer is
  // dirty; rail navigation routes through it (Save / Discard / Keep editing).
  const leaveGuardRef = useRef<((proceed: () => void) => void) | null>(null)
  const registerLeaveGuard = useCallback(
    (fn: ((proceed: () => void) => void) | null) => { leaveGuardRef.current = fn },
    [],
  )
  const guardedNav = useCallback((action: () => void) => {
    if (leaveGuardRef.current) leaveGuardRef.current(action)
    else action()
  }, [])

  const navTo = useCallback(
    (
      next: AnalyticsSection,
      opts?: { objName?: string; visualsTab?: 'mine' | 'standardised' },
    ) => {
      guardedNav(() => {
        setSection(next)
        setTarget((t) => ({
          objName: opts?.objName,
          visualsTab: opts?.visualsTab,
          nonce: t.nonce + 1,
        }))
      })
    },
    [guardedNav],
  )

  // ── Inventory (Overview cards + rail Visuals badge) ──
  const [inventory, setInventory] = useState<AnalyticsInventory>(EMPTY_INVENTORY)
  const [invLoading, setInvLoading] = useState(true)
  const [invError, setInvError] = useState<string | null>(null)
  const invToastShown = useRef(false)

  const reloadInventory = useCallback(async () => {
    if (!project) return
    setInvLoading(true)
    setInvError(null)
    const [conns, areas, schemas, vis, dash, reps, procs, pages, fore, std] =
      await Promise.allSettled([
        listBiConnections(project),
        listBusinessAreas(project),
        listVisualSchemas(project),
        objectCatalog(project, 'V'),
        objectCatalog(project, 'D'),
        listProjectReports(project),
        listProjectProcesses(project),
        listProjectPages(project),
        listForesight(project),
        listStandardizeRequests(project), // fail-soft: resolves null on error
      ])
    const next: AnalyticsInventory = {
      connections: conns.status === 'fulfilled' ? conns.value : null,
      areas: areas.status === 'fulfilled' ? (areas.value.business_areas || []) : null,
      schemas: schemas.status === 'fulfilled' ? (schemas.value.schemas || []) : null,
      visuals: vis.status === 'fulfilled' ? (vis.value.objects || []) : null,
      dashboards: dash.status === 'fulfilled' ? (dash.value.objects || []) : null,
      reports: reps.status === 'fulfilled' ? (reps.value.reports || []) : null,
      processes: procs.status === 'fulfilled' ? (procs.value.processes || []) : null,
      pages: pages.status === 'fulfilled' ? (pages.value.pages || []) : null,
      // Foresight is fail-soft like the standardize queue — its card shows a
      // dash rather than turning a missing ML backend into an inventory error.
      foresight: fore.status === 'fulfilled' ? fore.value : null,
      standardize: std.status === 'fulfilled' ? std.value : null,
    }
    setInventory(next)
    const core = [
      next.connections, next.areas, next.schemas, next.visuals, next.dashboards,
      next.reports, next.processes, next.pages,
    ]
    const coreFailed = core.filter((x) => x === null).length
    if (coreFailed === core.length) {
      setInvError('Could not load the analytics inventory — is the backend running?')
    } else if (coreFailed > 0 && !invToastShown.current) {
      invToastShown.current = true
      toast.error('Some analytics data could not be loaded — the affected cards show a dash.')
    }
    setInvLoading(false)
  }, [project])

  useEffect(() => { void reloadInventory() }, [reloadInventory])

  if (!currentProject) return null

  const visualsBadge = inventory.visuals ? inventory.visuals.length : null

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 118px)',
        minHeight: 540,
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-bg)',
      }}
    >
      {/* ── Left rail (236px, surface + right border — spec §3.1) ── */}
      <div
        style={{
          width: 236,
          flexShrink: 0,
          background: 'var(--color-bg-secondary)',
          borderRight: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 12px',
          minHeight: 0,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--color-accent)',
            padding: '0 6px 10px',
          }}
        >
          Analytics
        </div>

        <nav style={{ display: 'grid', gap: 2 }}>
          {RAIL_ITEMS.map((item) => {
            const active = section === item.key
            return (
              <button
                key={item.key}
                onClick={() => { if (!active) navTo(item.key) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '9px 12px',
                  borderRadius: 9,
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 15.5,
                  fontWeight: active ? 600 : 500,
                  background: active ? 'var(--color-accent-weak)' : 'transparent',
                  color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = 'var(--color-surface-2)'
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent'
                }}
              >
                <span
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: item.dot, flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.key === 'visuals' && visualsBadge !== null && (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '1px 7px',
                      borderRadius: 10,
                      background: 'var(--color-accent-weak)',
                      color: 'var(--color-accent)',
                    }}
                  >
                    {visualsBadge}
                  </span>
                )}
              </button>
            )
          })}
        </nav>

        <button
          className="btn btn-primary"
          style={{
            marginTop: 14,
            width: '100%',
            borderRadius: 9,
            justifyContent: 'center',
            padding: '10px 12px',
            fontSize: 14.5,
            fontWeight: 600,
          }}
          onClick={() => navTo('build')}
        >
          <Sparkles size={14} />
          Build analytics
        </button>

        <div style={{ flex: 1 }} />

        {/* "What the letters mean" legend */}
        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
          <button
            onClick={() => setLegendOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              padding: '7px 8px',
              borderRadius: 7,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13.5,
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-2)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            <ChevronRight
              size={13}
              style={{
                transform: legendOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.12s',
                flexShrink: 0,
              }}
            />
            What the letters mean
          </button>
          {legendOpen && (
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'grid', gap: 8, padding: '6px 8px 4px' }}>
              {OBJECT_TYPE_LEGEND.map((t) => (
                <div key={t.code} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <TypeChip code={t.code} size={18} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-faint)', lineHeight: 1.45 }}>{t.blurb}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Main area ── */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {section === 'overview' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <OverviewSection
              inventory={inventory}
              loading={invLoading}
              error={invError}
              onReload={() => void reloadInventory()}
              onNavigate={(s, opts) => navTo(s, opts)}
              onStartBuild={() => navTo('build')}
            />
          </div>
        )}

        {section === 'connections' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <ConnectionsSection
              project={project}
              onChanged={() => void reloadInventory()}
            />
          </div>
        )}

        {section === 'areas' && (
          <BusinessAreasSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'schema' && (
          <SchemaSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'visuals' && (
          <VisualsSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            initialTab={target.visualsTab}
            onGotoSection={(s) => navTo(s)}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'dashboards' && (
          <DashboardsSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'reports' && (
          <ReportsSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'processes' && (
          <ProcessesSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'smartpages' && (
          <SmartPagesSection
            project={project}
            registerLeaveGuard={registerLeaveGuard}
            initialObjName={target.objName}
            openNonce={target.nonce}
            onChanged={() => void reloadInventory()}
          />
        )}

        {section === 'foresight' && (
          <ForesightSection project={project} />
        )}

        {section === 'build' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <GuidedBuildSection
              project={project}
              onGotoSection={(s) => navTo(s)}
              onOpenObject={(s, objName) => navTo(s, { objName })}
              onFinished={() => {
                void reloadInventory()
                navTo('overview')
              }}
            />
          </div>
        )}
      </main>
    </div>
  )
}
